import { ORDER_STATUSES, WORK_ORDER_STATUSES, WORK_ORDER_TYPES } from "@bt/domain";
import {
  type Distribution,
  FEATURES,
  HISTOGRAM_EDGES,
  type InstallationReport,
  share,
  VISIT_DURATION_EDGES_SECONDS,
} from "@bt/domain/report";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BarList } from "../charts/bar-list";
import { ChartFrame, DataTable } from "../charts/frame";
import { type HeatRow, HeatTable } from "../charts/heat-table";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { type CoreOutputs, useTRPC } from "../trpc";
import { useView } from "../view";
import { InteractionsSection } from "./interactions";
import {
  areaLabel,
  BucketsFigure,
  CountsFigure,
  DistributionFigure,
  type FigureName,
  Grid,
  HistoryFigure,
  ReportView,
  Section,
  Stats,
  TallyFigure,
} from "./kit";

type Report = InstallationReport;
type Totals = CoreOutputs["usage"]["totals"];

/** The records garages keep, and how each block reads its figures. */
const RECORDS = [
  {
    kind: "parts",
    table: "parts",
    outlier: "parts",
    band: "parts.perGarage",
    added: "parts.added",
  },
  {
    kind: "vehicles",
    table: "vehicles",
    outlier: "vehicles",
    band: "vehicles.perGarage",
    added: "vehicles.added",
  },
  {
    kind: "workOrders",
    table: "workOrdersTotal",
    outlier: "work_orders",
    band: "workOrders.perGarage",
    added: "workOrders.added",
  },
  { kind: "templates", table: "templates", outlier: null, band: null, added: null },
  {
    kind: "orders",
    table: "orders",
    outlier: null,
    band: "orders.perGarage",
    added: "orders.placed",
  },
] as const;
type RecordKind = (typeof RECORDS)[number]["kind"];

/** How much is used, and how deeply (docs/admin-app/figures.md › Current state, Complex setups). */
export function UsagePage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      {RECORDS.map((record) => (
        <Section
          key={record.kind}
          title={t(`adminApp.usage.records.${record.kind}`)}
          description={t("adminApp.usage.recordHint")}
        >
          <ReportView>{(report) => <RecordBlock record={record} report={report} />}</ReportView>
        </Section>
      ))}
      <Section title={t("adminApp.usage.workOrdersAndOrders")}>
        <ReportView>{(report) => <WorkAndOrders report={report} />}</ReportView>
      </Section>
      <Section title={t("adminApp.usage.features")} description={t("adminApp.usage.featuresHint")}>
        <ReportView>{(report) => <Features report={report} />}</ReportView>
      </Section>
      <Section title={t("adminApp.usage.visits")} description={t("adminApp.usage.visitsHint")}>
        <Visits />
        <ReportView>{(report) => <Sessions report={report} />}</ReportView>
      </Section>
      <InteractionsSection />
      <Section
        title={t("adminApp.usage.procedures")}
        description={t("adminApp.usage.proceduresHint")}
      >
        <Procedures />
      </Section>
      <Section title={t("adminApp.usage.dropOuts")} description={t("adminApp.usage.dropOutsHint")}>
        <ReportView>{(report) => <DropOuts report={report} />}</ReportView>
      </Section>
      <Section
        title={t("adminApp.usage.complexSetups")}
        description={t("adminApp.usage.complexSetupsHint")}
      >
        <ReportView>{(report) => <ComplexSetups report={report} />}</ReportView>
      </Section>
    </div>
  );
}

function RecordBlock({ record, report }: { record: (typeof RECORDS)[number]; report: Report }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const section = report[record.kind as RecordKind] as
    { total?: number; perGarage?: { all?: Distribution; using?: Distribution } } | undefined;
  const all = section?.perGarage?.all;
  const adoption = all ? share(all.n - all.none, all.n) : null;
  const name = t(`adminApp.usage.records.${record.kind}`);
  const table = report.garageTable ?? [];
  const top = [...table]
    .sort((a, b) => Number(b[record.table] ?? 0) - Number(a[record.table] ?? 0))
    .filter((g) => Number(g[record.table] ?? 0) > 0)
    .slice(0, 10);
  if (!section) {
    return <p className="text-sm text-muted-foreground">{t("adminApp.usage.notReported")}</p>;
  }
  return (
    <div className="space-y-4">
      <Stats
        items={[
          { key: "total", label: t("adminApp.usage.total"), value: format.count(section.total) },
          {
            key: "adoption",
            label: t("adminApp.usage.adoption"),
            value: format.share(adoption),
            detail: t("adminApp.usage.adoptionHint"),
          },
        ]}
      />
      <Grid>
        <DistributionFigure
          title={t("adminApp.usage.perGarage", { record: name })}
          perGarage={section.perGarage}
          csvName={`${record.kind}-per-garage`}
        />
        {record.band ? (
          <HistoryFigure
            title={t("adminApp.usage.bandOverTime", { record: name })}
            description={t("adminApp.usage.bandHint")}
            csvName={`${record.kind}-band`}
            figures={[{ figure: record.band as FigureName, label: t("adminApp.usage.median") }]}
          />
        ) : null}
        {record.added ? (
          <HistoryFigure
            title={t("adminApp.usage.added", { record: name })}
            csvName={`${record.kind}-added`}
            figures={[{ figure: record.added as FigureName, label: name }]}
          />
        ) : null}
        {table.length ? (
          <ChartFrame
            title={t("adminApp.usage.top", { record: name })}
            description={t("adminApp.usage.topHint")}
            csvName={`${record.kind}-top`}
            columns={[
              { key: "garage", label: t("adminApp.usage.garage") },
              { key: "value", label: name, numeric: true },
              { key: "outlier", label: t("adminApp.usage.outlier") },
            ]}
            rows={top.map((g) => ({
              garage: g.name ?? g.id,
              value: Number(g[record.table] ?? 0),
              outlier:
                record.outlier && g.flags?.includes(`outlier:${record.outlier}`) ? "yes" : "",
            }))}
          >
            <BarList
              items={top.map((g) => ({
                key: g.id,
                label: g.name ?? g.id,
                value: Number(g[record.table] ?? 0),
                detail:
                  record.outlier && g.flags?.includes(`outlier:${record.outlier}`)
                    ? t("adminApp.usage.outlier")
                    : undefined,
              }))}
              formatValue={format.count}
              empty={t("adminApp.chart.noValues")}
            />
          </ChartFrame>
        ) : null}
      </Grid>
    </div>
  );
}

function WorkAndOrders({ report }: { report: Report }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const work = report.workOrders;
  const orders = report.orders;
  const status = (statuses: readonly string[], key: string) => (v: string) =>
    statuses.includes(v) ? t(`${key}.${v}` as "workOrderStatus.idea") : v;
  return (
    <div className="space-y-4">
      <Stats
        items={[
          {
            key: "fromTemplate",
            label: t("adminApp.usage.fromTemplate"),
            value: format.share(share(work?.fromTemplate ?? 0, work?.total ?? 0)),
          },
          {
            key: "ideas",
            label: t("adminApp.usage.oldIdeas"),
            value: format.count(work?.ideasOlderThan90Days),
          },
          {
            key: "planned",
            label: t("adminApp.usage.pastDate"),
            value: format.count(work?.plannedPastDate),
          },
          {
            key: "drafts",
            label: t("adminApp.usage.oldDrafts"),
            value: format.count(orders?.draftsOlderThan30Days),
          },
        ]}
      />
      <Grid>
        <TallyFigure
          title={t("adminApp.usage.workOrderStatus")}
          description={t("adminApp.usage.workOrderStatusHint")}
          rows={work?.byStatus}
          csvName="work-order-status"
          labelOf={status(WORK_ORDER_STATUSES, "workOrderStatus")}
        />
        <TallyFigure
          title={t("adminApp.usage.workOrderType")}
          rows={work?.byType}
          csvName="work-order-type"
          labelOf={status(WORK_ORDER_TYPES, "workOrderType")}
        />
        <DistributionFigure
          title={t("adminApp.usage.openPerGarage")}
          perGarage={work?.openPerGarage}
          csvName="open-work-orders"
        />
        <DistributionFigure
          title={t("adminApp.usage.daysToCompletion")}
          description={t("adminApp.usage.daysToCompletionHint")}
          perGarage={work?.daysToCompletion}
          csvName="days-to-completion"
        />
        <HistoryFigure
          title={t("adminApp.usage.completed")}
          csvName="completed"
          figures={[{ figure: "workOrders.completed", label: t("adminApp.usage.completed") }]}
        />
        <TallyFigure
          title={t("adminApp.usage.orderStatus")}
          rows={orders?.byStatus}
          csvName="order-status"
          labelOf={status(ORDER_STATUSES, "orderStatus")}
        />
        <DistributionFigure
          title={t("adminApp.usage.daysToReceive")}
          perGarage={orders?.daysToReceive}
          csvName="days-to-receive"
        />
        <DistributionFigure
          title={t("adminApp.usage.linesPerOrder")}
          perGarage={orders?.linesPerOrder}
          csvName="lines-per-order"
        />
        <HistoryFigure
          title={t("adminApp.usage.placedAndReceived")}
          csvName="placed-received"
          figures={[
            { figure: "orders.placed", label: t("adminApp.usage.placed") },
            { figure: "orders.received", label: t("adminApp.usage.received") },
          ]}
        />
      </Grid>
    </div>
  );
}

function Features({ report }: { report: Report }) {
  const { t } = useTranslation();
  const [feature, setFeature] = useState<(typeof FEATURES)[number]>("vehicles");
  const label = (f: string) => t(`adminApp.features.${f as "vehicles"}`);
  return (
    <Grid>
      <TallyFigure
        title={t("adminApp.usage.adoptionNow")}
        rows={report.featureAdoption?.features}
        csvName="feature-adoption"
        labelOf={label}
        base={report.featureAdoption?.garages}
      />
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("adminApp.usage.feature")}</span>
          <select
            className="h-8 rounded-md border border-input bg-surface px-2 text-sm"
            value={feature}
            onChange={(e) => setFeature(e.target.value as (typeof FEATURES)[number])}
          >
            {FEATURES.map((f) => (
              <option key={f} value={f}>
                {label(f)}
              </option>
            ))}
          </select>
        </label>
        <HistoryFigure
          key={feature}
          title={t("adminApp.usage.adoptionOverTime", { feature: label(feature) })}
          csvName={`feature-${feature}`}
          figures={[{ figure: `features.${feature}` as FigureName, label: label(feature) }]}
        />
      </div>
    </Grid>
  );
}

/** Totals of a metric by value under a prefix, e.g. visit.duration's "<device>:<edge>". */
function byValue(totals: Totals, metric: string): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of totals)
    if (row.metric === metric) out.set(row.value, (out.get(row.value) ?? 0) + row.total);
  return out;
}

/** A bucketed metric summed over devices, on its edges. */
function buckets(totals: Totals, metric: string, edges: readonly number[]): number[] {
  const counts = edges.map(() => 0);
  for (const [value, total] of byValue(totals, metric)) {
    const edge = Number(value.split(":").at(-1));
    const i = edges.indexOf(edge);
    if (i >= 0) counts[i] = (counts[i] ?? 0) + total;
  }
  return counts;
}

function Visits() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const { installation, range } = useView();
  const visits = useQuery(
    trpc.usage.totals.queryOptions({ name: installation, range, prefix: "visit" }),
  );
  const people = useQuery(
    trpc.usage.totals.queryOptions({ name: installation, range, prefix: "person." }),
  );
  const seconds = (n: number) => format.duration(n);
  return (
    <div className="space-y-4">
      <QueryView query={visits}>
        {(totals) => {
          const perDevice = [...byValue(totals, "visits")].map(([value, count]) => ({
            value,
            count,
          }));
          const total = perDevice.reduce((sum, d) => sum + d.count, 0);
          const withChange = [...byValue(totals, "visit.with_change").values()].reduce(
            (a, b) => a + b,
            0,
          );
          return (
            <div className="space-y-4">
              <Stats
                items={[
                  {
                    key: "visits",
                    label: t("adminApp.usage.visitCount"),
                    value: format.count(total),
                  },
                  {
                    key: "change",
                    label: t("adminApp.usage.withChange"),
                    value: format.share(share(withChange, total)),
                  },
                ]}
              />
              <Grid>
                <TallyFigure
                  title={t("adminApp.usage.byDevice")}
                  rows={perDevice.sort((a, b) => b.count - a.count)}
                  csvName="visits-by-device"
                  labelOf={(v) =>
                    ["phone", "tablet", "desktop", "unknown"].includes(v)
                      ? t(`adminApp.devices.${v as "phone"}`)
                      : v
                  }
                  base={total}
                />
                <BucketsFigure
                  title={t("adminApp.usage.duration")}
                  description={t("adminApp.usage.durationHint")}
                  counts={buckets(totals, "visit.duration", VISIT_DURATION_EDGES_SECONDS)}
                  edges={VISIT_DURATION_EDGES_SECONDS}
                  formatEdge={seconds}
                  continuous
                  csvName="visit-duration"
                />
                <BucketsFigure
                  title={t("adminApp.usage.activeTime")}
                  description={t("adminApp.usage.activeTimeHint")}
                  counts={buckets(totals, "visit.active", VISIT_DURATION_EDGES_SECONDS)}
                  edges={VISIT_DURATION_EDGES_SECONDS}
                  formatEdge={seconds}
                  continuous
                  csvName="visit-active"
                />
                <BucketsFigure
                  title={t("adminApp.usage.reads")}
                  description={t("adminApp.usage.readsHint")}
                  counts={buckets(totals, "visit.reads", HISTOGRAM_EDGES)}
                  edges={HISTOGRAM_EDGES}
                  formatEdge={String}
                  csvName="visit-reads"
                />
                <BucketsFigure
                  title={t("adminApp.usage.changes")}
                  counts={buckets(totals, "visit.changes", HISTOGRAM_EDGES)}
                  edges={HISTOGRAM_EDGES}
                  formatEdge={String}
                  csvName="visit-changes"
                />
              </Grid>
              {/* 24 columns need the full width. */}
              <HourOfWeek totals={totals} />
            </div>
          );
        }}
      </QueryView>
      <QueryView query={people}>
        {(totals) => (
          <Grid columns={3}>
            {(["person.visits", "person.active_minutes", "person.changes"] as const).map(
              (metric) => (
                <BucketsFigure
                  key={metric}
                  title={t(`adminApp.usage.personDay.${metric.slice(7) as "visits"}`)}
                  description={t("adminApp.usage.personDayHint")}
                  counts={buckets(totals, metric, HISTOGRAM_EDGES)}
                  edges={HISTOGRAM_EDGES}
                  formatEdge={String}
                  csvName={metric.replace(".", "-")}
                />
              ),
            )}
          </Grid>
        )}
      </QueryView>
    </div>
  );
}

/** When visits start, per hour of the week, shown in the browser's time zone. */
function HourOfWeek({ totals }: { totals: Totals }) {
  const { t, i18n } = useTranslation();
  const starts = byValue(totals, "visit.start");
  // The counts are UTC hours of the week from Monday 00:00; this browser's offset moves them.
  const offset = -new Date().getTimezoneOffset() / 60;
  const local = Array.from({ length: 168 }, () => 0);
  for (const [value, count] of starts) {
    const hour = (((Number(value) + offset) % 168) + 168) % 168;
    local[Math.floor(hour)] = (local[Math.floor(hour)] ?? 0) + count;
  }
  const max = Math.max(1, ...local);
  const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: "short", timeZone: "UTC" });
  const hours = Array.from({ length: 24 }, (_, h) => ({
    key: String(h),
    label: h % 3 === 0 ? String(h) : "",
  }));
  const rows: HeatRow[] = Array.from({ length: 7 }, (_, day) => ({
    key: String(day),
    // 2026-10-19 was a Monday.
    label: weekday.format(new Date(Date.UTC(2026, 9, 19 + day))),
    cells: hours.map((_, h) => {
      const count = local[day * 24 + h] ?? 0;
      return { share: count / max, text: count ? String(count) : "", title: String(count) };
    }),
  }));
  return (
    <ChartFrame
      title={t("adminApp.usage.hourOfWeek")}
      description={t("adminApp.usage.hourOfWeekHint")}
      csvName="visit-start"
      columns={[
        { key: "hour", label: t("adminApp.usage.hourUtc"), numeric: true },
        { key: "count", label: t("adminApp.kit.count"), numeric: true },
      ]}
      rows={[...starts].map(([hour, count]) => ({ hour: Number(hour), count }))}
    >
      <HeatTable
        caption={t("adminApp.usage.hourOfWeek")}
        rowHeader={t("adminApp.usage.day")}
        columns={hours}
        rows={rows}
        legend={["0", String(max)]}
        compact
      />
    </ChartFrame>
  );
}

function Sessions({ report }: { report: Report }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const sessions = report.signInSessions;
  return (
    <Grid>
      <Stats
        items={[
          {
            key: "live",
            label: t("adminApp.usage.liveSessions"),
            value: format.count(sessions?.live),
          },
        ]}
      />
      <DistributionFigure
        title={t("adminApp.usage.sessionsPerUser")}
        perGarage={sessions?.perUser}
        csvName="sessions-per-user"
      />
      <HistoryFigure
        title={t("adminApp.usage.signIns")}
        csvName="sign-ins"
        figures={[{ figure: "users.signIns", label: t("adminApp.usage.signIns") }]}
      />
      <CountsFigure
        title={t("adminApp.usage.signOuts")}
        source="usage"
        name="signouts"
        csvName="sign-outs"
      />
    </Grid>
  );
}

function Procedures() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const { installation, range } = useView();
  const query = useQuery(trpc.procedures.queryOptions({ name: installation, range }));
  return (
    <QueryView query={query}>
      {(data) => {
        const called = data.procedures.filter((p) => p.calls > 0);
        return (
          <Grid>
            <TallyFigure
              title={t("adminApp.usage.byArea")}
              rows={data.byArea}
              csvName="calls-by-area"
              labelOf={areaLabel(t)}
            />
            <TallyFigure
              title={t("adminApp.usage.mostUsed")}
              rows={called.slice(0, 10).map((p) => ({ value: p.path, count: p.calls }))}
              csvName="most-used"
            />
            <TallyFigure
              title={t("adminApp.usage.leastUsed")}
              rows={called
                .slice(-10)
                .reverse()
                .map((p) => ({ value: p.path, count: p.calls }))}
              csvName="least-used"
            />
            <ChartFrame
              title={t("adminApp.usage.neverCalled", { count: data.neverCalled.length })}
              description={t("adminApp.usage.neverCalledHint")}
              csvName="never-called"
              columns={[{ key: "path", label: t("adminApp.usage.procedure") }]}
              rows={data.neverCalled.map((path) => ({ path }))}
            >
              <NeverCalled paths={data.neverCalled} />
            </ChartFrame>
            <p className="col-span-2 text-xs text-muted-foreground">
              {t("adminApp.usage.callsTotal", {
                count: format.count(called.reduce((s, p) => s + p.calls, 0)),
              })}
            </p>
          </Grid>
        );
      }}
    </QueryView>
  );
}

function DropOuts({ report }: { report: Report }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <Stats
      items={[
        {
          key: "welcome",
          label: t("adminApp.usage.welcomeNotFinished"),
          value: format.count(report.friction?.welcomeNotFinished),
        },
        {
          key: "demo",
          label: t("adminApp.usage.demosNotStarted"),
          value: format.count(report.friction?.demosOpenedNotStarted),
        },
        {
          key: "untouched",
          label: t("adminApp.usage.untouchedParts"),
          value: format.count(report.parts?.untouched12Months),
        },
        {
          key: "prices",
          label: t("adminApp.usage.oldPrices"),
          value: format.count(report.suppliers?.pricesOlderThan12Months),
        },
      ]}
    />
  );
}

function ComplexSetups({ report }: { report: Report }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const setups = report.complexSetups;
  const depth = setups?.depth ?? [];
  const most = report.activity?.mostUsed ?? [];
  const columns = [
    { key: "measure", label: t("adminApp.kit.measure") },
    { key: "median", label: t("adminApp.usage.median"), numeric: true },
    { key: "p90", label: "p90", numeric: true },
    { key: "max", label: "max", numeric: true },
    { key: "top", label: t("adminApp.usage.topGarages") },
  ];
  const rows = depth.map((d) => ({
    measure: t(`adminApp.depth.${d.measure as "members"}`, { defaultValue: d.measure }),
    median: format.decimal(d.perGarage?.median),
    p90: format.decimal(d.perGarage?.p90),
    max: format.decimal(d.perGarage?.max),
    top: (d.top ?? [])
      .slice(0, 3)
      .map((g) => `${g.garage} (${format.decimal(g.value)})`)
      .join(", "),
  }));
  return (
    <div className="space-y-4">
      <Grid>
        <DistributionFigure
          title={t("adminApp.usage.breadth")}
          description={t("adminApp.usage.breadthHint")}
          perGarage={setups?.breadth}
          csvName="breadth"
        />
        <BucketsFigure
          title={t("adminApp.usage.breadthCounts")}
          counts={setups?.breadthCounts}
          edges={FEATURES.map((_, i) => i).concat(FEATURES.length)}
          formatEdge={String}
          csvName="breadth-counts"
        />
      </Grid>
      <ChartFrame
        title={t("adminApp.usage.depth")}
        description={t("adminApp.usage.depthHint")}
        csvName="depth"
        columns={columns}
        rows={rows}
      >
        <DataTable columns={columns} rows={rows} />
      </ChartFrame>
      <TallyFigure
        title={t("adminApp.usage.mostActive")}
        description={t("adminApp.usage.mostActiveHint")}
        rows={most.map((m) => ({
          value: m.garage.name ?? m.garage.id,
          count: m.activeDays30 ?? 0,
        }))}
        csvName="most-active"
      />
    </div>
  );
}

/** The procedures nobody called: the first 20, and all of them on request. */
function NeverCalled({ paths }: { paths: readonly string[] }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const shown = all ? paths : paths.slice(0, 20);
  return (
    <>
      <ul className="columns-2 text-xs text-muted-foreground">
        {shown.map((path) => (
          <li key={path} className="truncate font-mono">
            {path}
          </li>
        ))}
      </ul>
      {paths.length > 20 ? (
        <button
          type="button"
          className="mt-2 text-sm font-medium text-brand-fg hover:underline"
          onClick={() => setAll((v) => !v)}
        >
          {all
            ? t("adminApp.kit.showFewer", { count: 20 })
            : t("adminApp.kit.showAll", { count: paths.length })}
        </button>
      ) : null}
    </>
  );
}
